import {
  ConsoleState,
  findDraft,
  groupRoutes,
  liveRevision,
  publishBlockers,
  scopeInheritsShared,
  vendorGroups,
} from './store';
import { Draft, Revision, RouteGroup } from './types';
import { casaBusinesses } from './directory-pos';

/**
 * How deep the workspace is looking, and at what.
 *
 * Every level but `route` is a plain board of boxes; `route` is the real Camel flow on Kaoto's
 * own canvas. A vendor holds two boards — the route groups it has configured, and the tenants
 * connected to it — so the level carries which of the two is showing.
 */
export type CanvasLevel =
  /** The top: what the platform is being used for, of which vendor routing is one. */
  | { kind: 'uses' }
  | { kind: 'vendors' }
  | { kind: 'vendor'; vendorId: string; scope: string; tab?: VendorTab }
  | { kind: 'tenant'; vendorId: string; tenantId: string; businessId?: string }
  | { kind: 'group'; vendorId: string; groupId: string; scope: string }
  | { kind: 'route'; vendorId: string; groupId: string; routeId: string; scope: string };

export type VendorTab = 'groups' | 'tenants';

export type OverviewAction =
  | { kind: 'navigate'; level: CanvasLevel }
  | { kind: 'create'; entity: 'vendor' | 'group' | 'route' | 'tenant' | 'business' }
  | { kind: 'library'; library: 'templates' | 'schemas' | 'transforms' | 'ingress' | 'tenants' };

export const visibleGroups = (state: ConsoleState, vendorId: string, scope: string): RouteGroup[] => {
  const groups = vendorGroups(state, vendorId);

  if (scope.startsWith('business:')) {
    const business = state.businesses?.find((item) => item.id === scope.slice(9));
    if (!business) return [];
    const held = new Set(visibleGroups(state, vendorId, `tenant:${business.tenantId}`).map((group) => group.id));
    const chosen = state.businessGroups?.find((item) => item.vendorId === vendorId && item.businessId === business.id);

    return chosen ? groups.filter((group) => held.has(group.id) && chosen.groupIds.includes(group.id)) : [];
  }

  const tenantId = scope.startsWith('tenant:') ? scope.slice(7) : undefined;
  const selected = state.tenantGroups?.find((item) => item.vendorId === vendorId && item.tenantId === tenantId);

  return selected ? groups.filter((group) => selected.groupIds.includes(group.id)) : groups;
};

/**
 * The endpoints a business unit may be sent traffic for.
 *
 * The runtime gate is derived from this same function, so the console and the door can never
 * disagree about what a business holds. Keyed by route id rather than route key: a route key is
 * built from vendor, group and route *names*, so renaming a group would silently revoke access.
 */
export const entitledRouteIds = (state: ConsoleState, vendorId: string, businessId: string): string[] =>
  visibleGroups(state, vendorId, `business:${businessId}`).flatMap((group) =>
    groupRoutes(state, group.id).map((route) => route.id),
  );

/** Businesses of this tenant that would be refused everything, so they can be fixed before they are. */
export const refusedBusinesses = (state: ConsoleState, vendorId: string, tenantId: string) =>
  casaBusinesses(state, vendorId, tenantId).filter((business) => entitledRouteIds(state, vendorId, business.id).length === 0);

export const isTenantOwned = (state: ConsoleState, vendorId: string, tenantId: string, groupId: string): boolean =>
  Boolean(
    state.tenantOwnedGroups
      ?.find((item) => item.vendorId === vendorId && item.tenantId === tenantId)
      ?.groupIds.includes(groupId),
  );

export type ScopeLevelKind = 'business' | 'tenant' | 'shared';

export interface ScopeLevel {
  scope: string;
  kind: ScopeLevelKind;
  name: string;
  draft?: Draft;
  live?: Revision;
}

export interface ScopeResolution {
  /** Narrowest first, mirroring the plane's own business → tenant → shared walk. */
  levels: ScopeLevel[];
  authoredAt?: ScopeLevel;
  servingAt?: ScopeLevel;
}

/**
 * Which level a route's configuration comes from, in the order the data plane resolves it.
 *
 * Authored and serving are separate answers: a level can hold a draft nobody published while a
 * wider level is what actually runs. Answering them in one walk is what stops the row saying one
 * level configures it and another serves it.
 */
export const resolveScopeChain = (state: ConsoleState, routeId: string, scope: string): ScopeResolution => {
  const route = state.routes.find((item) => item.id === routeId);
  const vendorName = state.vendors.find((item) => item.id === route?.vendorId)?.name ?? route?.vendorId ?? 'the vendor';

  const at = (levelScope: string, kind: ScopeLevelKind, name: string): ScopeLevel => ({
    scope: levelScope,
    kind,
    name,
    draft: findDraft(state, routeId, levelScope),
    live: liveRevision(state, routeId, levelScope),
  });

  const tenantName = (id: string) => state.tenants.find((item) => item.id === id)?.name ?? id;

  const levels: ScopeLevel[] = [];
  let tenantId: string | undefined;

  if (scope.startsWith('business:')) {
    const businessId = scope.slice('business:'.length);
    const business = (state.businesses ?? []).find((item) => item.id === businessId);
    levels.push(at(scope, 'business', business?.name ?? businessId));
    tenantId = business?.tenantId;
  } else if (scope.startsWith('tenant:')) {
    tenantId = scope.slice('tenant:'.length);
  }

  if (tenantId) levels.push(at(`tenant:${tenantId}`, 'tenant', tenantName(tenantId)));

  /* A scope whose tenant is not connected to the vendor falls back to nothing. */
  if (scope === 'shared' || scopeInheritsShared(state, routeId, scope))
    levels.push(at('shared', 'shared', vendorName));

  return {
    levels,
    authoredAt: levels.find((level) => level.draft),
    servingAt: levels.find((level) => level.live),
  };
};

/**
 * What the selected scope actually holds for an endpoint.
 *
 * A child scope that has authored nothing runs a wider one's configuration, which is a different
 * thing from having nothing to run at all — and both look identical on a canvas that shows only
 * a name. So each endpoint says which of the two it is, and whether what it holds can publish.
 */
export type ScopeStatus = 'missing' | 'invalid' | 'inherited' | 'overridden' | 'configured';

export const scopeStatus = (state: ConsoleState, routeId: string, scope: string): ScopeStatus => {
  const own = findDraft(state, routeId, scope);
  if (!own) {
    if (scope === 'shared') return 'missing';

    return resolveScopeChain(state, routeId, scope)
      .levels.slice(1)
      .some((level) => level.draft)
      ? 'inherited'
      : 'missing';
  }
  if (publishBlockers(state, routeId, scope).length > 0) return 'invalid';

  return scope === 'shared' ? 'configured' : 'overridden';
};

const statusLabels: Record<ScopeStatus, string> = {
  missing: 'not configured',
  invalid: 'needs attention',
  inherited: 'inherited from vendor-wise',
  overridden: 'overridden here',
  configured: 'configured',
};

/** The full phrase. There is room for it beside the actions, so the toolbar uses this one. */
export const scopeStatusLabel = (status: ScopeStatus, sourceName?: string): string =>
  status === 'inherited' && sourceName ? `inherited from ${sourceName}` : statusLabels[status];

const statusWords: Record<ScopeStatus, string> = {
  missing: 'missing',
  invalid: 'invalid',
  inherited: 'inherited',
  overridden: 'overridden',
  configured: 'configured',
};

/**
 * The one-word form, for a canvas node.
 *
 * Kaoto labels a node from its step `description` — the default `nodeLabel` setting is
 * `Description` — and draws it on one line of `DEFAULT_LABEL_WIDTH` (150px) with an ellipsis. So
 * the phrase above does not fit beside an endpoint's name: `Invoice · inherited from vendor-wise`
 * renders as `Invoice · inherited fr…`, which loses exactly the word that mattered.
 */
export const scopeStatusWord = (status: ScopeStatus): string => statusWords[status];

/**
 * The nearest level that still exists, or nothing when the level is already fine.
 *
 * The level is remembered between sessions, so it can name a tenant, a vendor or a route group
 * that has since been removed — most easily by removing one while standing inside it. Drawing a
 * board for something that is not there shows an empty page with no hint of why, so the board
 * climbs to the nearest step that is real instead.
 *
 * A route level is left alone: that one is Kaoto's canvas, and pulling it away mid-edit would
 * cost the work in front of the person.
 */
export const nearestExistingLevel = (state: ConsoleState, level: CanvasLevel): CanvasLevel | undefined => {
  if (level.kind === 'uses' || level.kind === 'vendors' || level.kind === 'route') return undefined;

  if (!state.vendors.some((vendor) => vendor.id === level.vendorId)) return { kind: 'vendors' };

  if (level.kind === 'vendor') return undefined;

  if (level.kind === 'group')
    return state.groups.some((group) => group.id === level.groupId)
      ? undefined
      : { kind: 'vendor', vendorId: level.vendorId, scope: 'shared' };

  if (!state.tenants.some((tenant) => tenant.id === level.tenantId))
    return { kind: 'vendor', vendorId: level.vendorId, scope: 'shared', tab: 'tenants' };

  if (level.businessId && !(state.businesses ?? []).some((business) => business.id === level.businessId))
    return { kind: 'tenant', vendorId: level.vendorId, tenantId: level.tenantId };

  return undefined;
};
